/**
 * MaisonInitBanner — V14.62 Sprint 26 (auto-seed in V14.63 Sprint 26 polish).
 *
 * Displayed at the top of the Édition Plan station when the project is
 * a "Maison individuelle" (`project_type_code === 'house_single'`) AND
 * the project has not yet been initialized with a building / floors /
 * lots structure.
 *
 * V14.63 — AUTO-SEED policy : when status returns initialized=false, the
 * banner automatically calls /maison/quick-setup once and disappears,
 * trading away the "Initialiser les étages" button for a frictionless
 * onboarding. A toast confirms the seed. The legacy manual button is
 * still rendered for 2.5s while seeding so the user sees what just
 * happened (and in case auto-seed fails, they get a retry handle).
 *
 * Once initialized, the banner switches to a compact green confirmation
 * badge so the user knows the Maison mode is active.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Home, Loader2, Sparkles, CheckCircle2 } from 'lucide-react';
import { isMaisonProject } from '../../../lib/projectMode';
import { formatApiError } from '../../../lib/formatApiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaisonInitBanner = ({ project, onSeeded }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  // V14.63 — ensure auto-seed fires at most ONCE per project per page session.
  const autoSeededRef = useRef(false);

  useEffect(() => {
    if (!isMaisonProject(project) || !project?.id) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/projects/${project.id}/maison/status`,
      { withCredentials: true })
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => { if (!cancelled) setStatus(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [project]);

  const seedNow = async (silent = false) => {
    setSeeding(true);
    try {
      const { data } = await axios.post(
        `${API}/projects/${project.id}/maison/quick-setup`,
        {},
        { withCredentials: true },
      );
      const description = `${data.floors_seeded.length} étages provisionnés (${data.floors_seeded.map((f) => f.code).join(' · ')})`;
      if (silent) {
        toast.success('Étages Maison initialisés automatiquement', { description });
      } else {
        toast.success('Maison initialisée', { description });
      }
      setStatus({
        is_maison: true,
        initialized: true,
        buildings_count: 1,
        floors_count: data.floors_seeded.length,
        lots_count: data.floors_seeded.length,
        floors: data.floors_seeded.map((f) => ({
          floor_id: f.floor_id, name: f.name, level: f.level,
        })),
      });
      if (onSeeded) onSeeded(data);
    } catch (err) {
      // Don't toast on silent failures — the user can still retry manually
      // via the button that becomes visible below.
      if (!silent) toast.error(formatApiError(err, "Échec de l'initialisation"));
      autoSeededRef.current = false; // allow a manual retry
    } finally {
      setSeeding(false);
    }
  };

  // V14.63 — Trigger AUTO-SEED once the status confirms the project is a
  // non-initialized Maison. Bypasses the manual button entirely.
  useEffect(() => {
    if (!isMaisonProject(project) || !project?.id) return;
    if (loading || seeding) return;
    if (autoSeededRef.current) return;
    if (status && status.initialized === false) {
      autoSeededRef.current = true;
      seedNow(/* silent */ true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, loading, seeding, project]);

  if (!isMaisonProject(project)) return null;
  if (loading) return null;
  if (status?.initialized) {
    return (
      <div className="inline-flex items-center gap-2 text-[10.5px] px-3 py-1.5 rounded-md border self-start"
           style={{ borderColor: 'rgba(16,185,129,0.35)',
                    background: 'rgba(16,185,129,0.08)',
                    color: 'rgb(167, 243, 208)' }}
           data-testid="maison-mode-badge">
        <Home className="w-3.5 h-3.5" />
        <span>Mode Maison · <span className="font-mono">{status.floors_count}</span> étages · <span className="font-mono">{status.lots_count}</span> lots</span>
        <CheckCircle2 className="w-3 h-3" />
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border p-4 flex items-start gap-3"
      style={{ borderColor: 'rgba(251,191,36,0.4)',
               background: 'linear-gradient(135deg, rgba(120,53,15,0.18), rgba(2,6,12,0.6))' }}
      data-testid="maison-init-banner"
    >
      <Sparkles className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#fbbf24' }} />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-amber-200" data-testid="maison-init-title">
          {seeding
            ? 'Initialisation automatique des étages…'
            : 'Initialisez les étages de la maison'}
        </div>
        <div className="text-[11px] text-gray-300 mt-1 leading-relaxed">
          {seeding ? (
            <>Provisionnement des 4 niveaux standards <span className="font-mono">SS · RDC · R+1 · Combles</span>, patientez…</>
          ) : (
            <>Ce projet est tagué <span className="font-mono text-amber-200">Maison individuelle</span> mais
              n&apos;a pas encore de structure. Cliquez ci-contre pour réessayer le provisionnement
              automatique des 4 niveaux <span className="font-mono">SS · RDC · R+1 · Combles</span>.</>
          )}
        </div>
        <div className="text-[10px] text-gray-500 mt-1.5 italic">
          Étapes ultérieures : tracé du terrain (RDC), escaliers & cohérence verticale,
          vue extérieure 3D dans Validation.
        </div>
      </div>
      <button
        type="button"
        onClick={() => seedNow(false)}
        disabled={seeding}
        data-testid="maison-init-seed-btn"
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded text-[11px] font-semibold bg-amber-500 hover:bg-amber-400 text-amber-950 disabled:opacity-40 disabled:cursor-not-allowed self-start"
      >
        {seeding
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Initialisation…</>
          : <><Home className="w-3.5 h-3.5" /> Réessayer</>}
      </button>
    </section>
  );
};

export default MaisonInitBanner;
